import React, { useEffect, useState, useMemo, useCallback } from "react";
import { shiftType } from "services/shiftsService";
import {
	Tooltip,
	OverlayTrigger,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { EmployeeLink } from "components/Employees/EmployeeEditDrawer";
import { FaMapMarkerAlt, FaRegComment, FaKeyboard, FaGlobe } from "react-icons/fa";
import { FiAlertTriangle, FiInfo } from "react-icons/fi";
import { GoAlert } from "react-icons/go";
import { FaMobileAlt, FaDesktop } from "react-icons/fa";
import { BsCreditCard2Front } from "react-icons/bs";
import { GrRadialSelected } from "react-icons/gr";
import moment from "moment";
import { formatLocalizedDate } from "utils/dateConfig";
import Switch from "components/Switch";
import { useEmployeeDayTable } from "../../hooks/attendance/useEmployeeDayTable";
import {
	calculateDuration,
	formatSeconds,
	formatDecimalHoursAsHm,
	formatBalanceDeltaHmOrSeconds,
	getPlannedShiftIntervals,
	getTooltipAlertContent,
} from "./utils/tableUtils";
import "./Table.scss";
import TimeObservationEdit from "components/TimeRegistrations/TimeObservationEditModal";
import TimeRegistrationLogModal from "components/TimeRegistrations/TimeRegistrationLogModal";
import { useDispatch } from "react-redux";
import { showModal } from "actions/modalActions";
import { useTranslation } from "react-i18next";
import { getDiffTime } from "utils/datetime";
import { hasTimeChatOrAttachments, isTempTimeRegistration } from "utils/timeChatUtils";
import { ReactComponent as AlertIcon } from "assets/icons/alert.svg";
import { RiEyeLine } from "react-icons/ri";
import DataTable from "components/DataTable";

const SORTABLE_FIELDS = {
	employee: true,
	day: true,
	start: true,
	end: true,
	worked_time: true,
	type: true,
	approved: true,
};

const isSortableField = (field) => SORTABLE_FIELDS[field] === true;

/** Aplana grupos + filas opcionales de descanso para DataTable. */
function buildFlatRows(groupedItems, showRestTime) {
	if (!groupedItems?.length) return [];
	const out = [];
	for (const group of groupedItems) {
		group.records.forEach((record, recordIndex) => {
			if (showRestTime && recordIndex > 0) {
				const prevRecord = group.records[recordIndex - 1];
				if (prevRecord.end && record.start) {
					const diff = getDiffTime(prevRecord.end, record.start);
					if (diff > 0) {
						const startT = moment(prevRecord.end);
						const endT = moment(record.start);
						const duration = moment.duration(endT.diff(startT));
						const hours = Math.floor(duration.asHours());
						const minutes = Math.floor(duration.asMinutes() % 60);
						const formattedDiff = `${hours}h ${minutes.toString().padStart(2, "0")}'`;
						out.push({
							rowKind: "rest",
							restKey: `rest-${record.id}`,
							durationLabel: formattedDiff,
						});
					}
				}
			}
			out.push({
				rowKind: "record",
				rowKey: record.id,
				group,
				record,
				recordIndex,
			});
		});
	}
	return out;
}

/** Tooltip de balance (vista por día) — layout alineado con diseño producto. */
function EmployeeDayBalanceTooltip({ group, timeRegistrationBalanceCalculation }) {
	const { t } = useTranslation();
	const basisPhrase =
		timeRegistrationBalanceCalculation === "TARGET_HOURS"
			? t('times.balance.targetHours')
			: t('times.balance.plannedHours');
	const totalLabel =
		group.totalSeconds > 0 ? formatSeconds(group.totalSeconds, "hm") : "—";
	const { short_raw: shortRaw, short: shortLabel } = group.balanceObj;
	const deficitSeconds =
		shortRaw < 0 ? Math.round(Math.abs(shortRaw) * 3600) : 0;
	let deficitFormatted = "";
	if (deficitSeconds > 0) {
		deficitFormatted =
			deficitSeconds < 60
				? `${deficitSeconds}s`
				: formatSeconds(deficitSeconds, "hm");
	}

	return (
		<div className="tooltip-balance__inner">
			<div className="tooltip-balance__rows">
				<div className="tooltip-balance__row">
					<span className="tooltip-balance__label">{t('times.balance.targetLabel')}</span>
					<span className="tooltip-balance__value">
						{formatDecimalHoursAsHm(group.objectiveHoursValue)}
					</span>
				</div>
				<div className="tooltip-balance__row">
					<span className="tooltip-balance__label">{t('times.balance.plannedLabel')}</span>
					<span className="tooltip-balance__value">
						{formatDecimalHoursAsHm(group.plannedHoursValue)}
					</span>
				</div>
			</div>

			{shortRaw > 0 && (
				<>
					<hr className="tooltip-balance__divider" />
					<div className="tooltip-balance__status tooltip-balance__status--excess">
						<div className="tooltip-balance__status-title">
							<span className="tooltip-balance__status-icon-wrap" aria-hidden>
								<FiAlertTriangle />
							</span>
							<span>{t('attendance.excessShift')}</span>
						</div>
						<p className="tooltip-balance__status-detail">
							<b>{formatBalanceDeltaHmOrSeconds(shortRaw)}</b> {t('times.balance.accordingTo')} {basisPhrase}
						</p>
					</div>
				</>
			)}

			{shortRaw < 0 && (
				<>
					<hr className="tooltip-balance__divider" />
					<div className="tooltip-balance__status tooltip-balance__status--deficit">
						<div className="tooltip-balance__status-title">
							<span className="tooltip-balance__status-icon-wrap" aria-hidden>
								<FiInfo />
							</span>
							<span>{t('attendance.incompleteShift')}</span>
						</div>
						<p className="tooltip-balance__status-detail">
							{t('times.balance.shortText')} <b>{deficitFormatted || formatBalanceDeltaHmOrSeconds(shortRaw).replace(/^[-+]/, "")}</b>{" "}
							{t('times.balance.shortSuffix')} {basisPhrase}
						</p>
					</div>
				</>
			)}
		</div>
	);
}

const EmployeeDayTable = () => {
	const { t } = useTranslation();
	const {
		items,
		loading,
		loadingMore,
		hasMore,
		fetchMoreData,
		applySort,
		sortConfig,
		handleApprovalWithError,
		handleHideModal,
		groupedItems,
		showRestTime,
		timeRegistrationBalanceCalculation,
		plannedShifts,
		approvingIds,
	} = useEmployeeDayTable();

	const dispatch = useDispatch();
	const [hasScroll, setHasScroll] = useState(false);

	const openLogsModal = useCallback((time) => {
		dispatch(
			showModal({
				modalProps: {
					open: true,
					title: t('times.logModal.title'),
					style: { width: "900px" },
					content: (
						<TimeRegistrationLogModal
							time={time}
							handleClose={handleHideModal}
						/>
					),
					closeModal: handleHideModal,
				},
				modalType: "edit",
			})
		);
	}, [dispatch, handleHideModal]);

	const openObservationsModal = useCallback((time, titleObservations) => {
		if (isTempTimeRegistration(time)) {
			dispatch(
				showModal({
					modalProps: {
						open: true,
						style: { width: "400px" },
						title: t("times.registrations.addErrorTitle"),
						message: t("times.chat.saveTimeBeforeMessage"),
						closeModal: handleHideModal,
					},
					modalType: "alert",
				})
			);
			return;
		}

		dispatch(
			showModal({
				modalProps: {
					open: true,
					title: titleObservations,
					style: { width: "520px", maxWidth: "95vw" },
					content: (
						<TimeObservationEdit
							time={time}
							employee={time.employee}
							handleClose={handleHideModal}
						/>
					),
					closeModal: handleHideModal,
				},
				modalType: "edit",
			})
		);
	}, [dispatch, handleHideModal, t]);

	const renderDeviceIcon = (record, type = "start") => {
		const deviceOrigin = type === "start" ? record.start_origin : record.end_origin;
		if (!deviceOrigin) return null;

		let icon = null;
		let tooltipText = deviceOrigin;

		if (deviceOrigin.toLowerCase() === "terminal") {
			icon = <BsCreditCard2Front className="device-icon terminal" />;
			const terminalCode = record.terminal_code2 || "desconocido";
			tooltipText = (
				<div>
					<div>{deviceOrigin}</div>
					<div>{t('attendance.terminalCode', { code: terminalCode })}</div>
				</div>
			);
		} else {
			const isMobile =
				deviceOrigin.toLowerCase().includes("app") ||
				deviceOrigin.toLowerCase().includes("mobile") ||
				deviceOrigin.toLowerCase().includes("android") ||
				deviceOrigin.toLowerCase().includes("ios");

			icon = isMobile ? (
				<FaMobileAlt className="device-icon mobile" />
			) : (
				<FaDesktop className="device-icon desktop" />
			);
		}

		return (
			<OverlayTrigger
				placement="top"
				overlay={<Tooltip id={`device-${record.id}-${type}`}>{tooltipText}</Tooltip>}
			>
				<span style={{ marginLeft: "6px", display: "inline-block" }}>
					{React.cloneElement(icon, {
						style: { color: "#6c757d", cursor: "pointer" },
					})}
				</span>
			</OverlayTrigger>
		);
	};

	const renderLocationIcon = (record) => {
		const hasLocation =
			record.location ||
			(record.start_lat && record.start_lon) ||
			(record.end_lat && record.end_lon);

		if (!hasLocation) return null;

		const ip = record.ip || "N/A";
		const lat = record.start_lat || record.end_lat || "N/A";
		const lon = record.start_lon || record.end_lon || "N/A";

		return (
			<OverlayTrigger
				trigger="click"
				rootClose
				placement="top"
				overlay={(props) => (
					<Tooltip
						id={`tooltip-location-${record.id}`}
						{...props}
						className="location-tooltip"
					>
						<div className="popover-content">
							<ul>
								<li>
									<strong>IP:</strong> {ip}
								</li>
								<li>
									<strong>LAT:</strong> {lat}
								</li>
								<li>
									<strong>LNG:</strong> {lon}
								</li>
							</ul>
						</div>
						{lat !== "N/A" && lon !== "N/A" && (
							<button
								type="button"
								className="btn btn-link link-pink"
								onClick={() =>
									window.open(
										`https://www.google.com/maps?q=${lat},${lon}`,
										"_blank"
									)
								}
								style={{
									padding: "0",
									margin: "2px 0 0 0",
									fontSize: "0.8rem",
								}}
							>
								Ver en maps
							</button>
						)}
					</Tooltip>
				)}
			>
				<span style={{ marginLeft: "6px", display: "inline-block" }}>
					<FaMapMarkerAlt
						className="location-icon"
						style={{ color: "#6c757d", cursor: "pointer" }}
					/>
				</span>
			</OverlayTrigger>
		);
	};

	const renderSourceIcon = (record, type = "start") => {
		const source = type === "start" ? record.start_source : record.end_source;
		if (!source) return null;

		let icon = null;
		let tooltipText = "";

		switch (source) {
			case "HOMEPAGE_WIDGET":
				icon = <GrRadialSelected className="source-icon" />;
				tooltipText = t('times.device.clockButton');
				break;
			case "TIME_REGISTRATIONS":
				icon = <FaKeyboard className="source-icon" />;
				tooltipText = t('times.device.keyboard');
				break;
			case "TERMINAL": {
				// renderDeviceIcon already shows the card icon when origin is terminal
				const deviceOrigin =
					type === "start" ? record.start_origin : record.end_origin;
				if (deviceOrigin && deviceOrigin.toLowerCase() === "terminal") {
					return null;
				}
				icon = <BsCreditCard2Front className="source-icon" />;
				tooltipText = t('times.device.terminal');
				break;
			}
			case "WEB_TERMINAL":
				icon = <FaGlobe className="source-icon" />;
				tooltipText = t('times.device.webTerminal');
				break;
			case "MODIFIED":
				return null;
			default:
				return null;
		}

		return (
			<OverlayTrigger
				placement="top"
				overlay={<Tooltip id={`source-${record.id}-${type}`}>{tooltipText}</Tooltip>}
			>
				<span style={{ marginLeft: "6px", display: "inline-block" }}>
					{React.cloneElement(icon, {
						style: { color: "#6c757d", cursor: "pointer" },
					})}
				</span>
			</OverlayTrigger>
		);
	};

	const renderModifiedIcon = (record, type = "start") => {
		const source = type === "start" ? record.start_source : record.end_source;
		if (source !== "MODIFIED") return null;

		return (
			<OverlayTrigger
				placement="top"
				overlay={<Tooltip id={`modified-${record.id}-${type}`}>{t('attendance.modifiedHistory')}</Tooltip>}
			>
				<span
					role="button"
					tabIndex={0}
					style={{ marginLeft: "6px", display: "inline-flex", alignItems: "center", cursor: "pointer" }}
					onClick={() => openLogsModal(record)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							openLogsModal(record);
						}
					}}
				>
					<AlertIcon style={{ width: "20px", height: "auto" }} />
				</span>
			</OverlayTrigger>
		);
	};

	const renderAlertIcon = (record) => {
		const validAlerts =
			record.alerts && record.alerts.length > 0
				? record.alerts.filter((alert) => alert && (alert.type || alert.description))
				: [];

		if (validAlerts.length === 0) return null;

		const MAX_TOOLTIP_ALERTS = 10;
		const displayAlerts = validAlerts.slice(0, MAX_TOOLTIP_ALERTS);
		const hasMoreAlerts = validAlerts.length > MAX_TOOLTIP_ALERTS;

		const iconStyle = {
			display: "inline-block",
			fontSize: "18px",
			color: validAlerts.some((a) => a && a.type === "warning")
				? "#f0ad4e"
				: "#d9534f",
			cursor: "pointer",
		};

		return (
			<OverlayTrigger
				placement="top"
				container={document.body}
				popperConfig={{
					modifiers: [
						{
							name: "preventOverflow",
							options: { boundary: document.body },
						},
					],
				}}
				overlay={
					<Tooltip
						id={`tooltip-alert-${record.employeeId}-${record.day}`}
						className="tooltip-alerta"
					>
						<ul>
							{getTooltipAlertContent(displayAlerts, t)}
							{hasMoreAlerts && (
								<li>
									<em>...y {validAlerts.length - MAX_TOOLTIP_ALERTS} más</em>
								</li>
							)}
						</ul>
					</Tooltip>
				}
			>
				<span className="alert-wrapper" style={iconStyle}>
					{validAlerts.some((alert) => alert.type === "warning") ? (
						<FiAlertTriangle className="warning-icon" />
					) : (
						<GoAlert className="alert-icon" />
					)}
				</span>
			</OverlayTrigger>
		);
	};

	const renderObservationsIcon = (item) => {
		if (!hasTimeChatOrAttachments(item)) return null;
		const titleObservations = t('times.table.viewObservations');

		return (
			<FaRegComment
				className="icon-comment"
				title={t('times.chat.viewConversation')}
				onClick={() => openObservationsModal(item, titleObservations)}
			/>
		);
	};

	const flatRows = useMemo(
		() => buildFlatRows(groupedItems, showRestTime),
		[groupedItems, showRestTime]
	);

	const sortTh = (field, baseClass) =>
		`${baseClass}${isSortableField(field) ? " sortable" : ""}`;

	const columns = [
		{
			key: "employee",
			header: t('common.employee'),
			className: "employee-cell",
			headerClassName: sortTh("employee", "employee-cell "),
			sortable: isSortableField("employee"),
			render: (row) => {
				const { group, record, recordIndex } = row;
				if (recordIndex !== 0) return null;
				return (
					<div className="employee-info-wrapper">
						<div className="employee-avatar-wrapper">
							{record.employee?.name ? record.employee.name.charAt(0).toUpperCase() : ""}
							{record.employee?.surname ? record.employee.surname.charAt(0).toUpperCase() : ""}
						</div>
						<div className="employee-details">
							<div className="flex flex-row">
								<EmployeeLink
									employee={record.employee}
									employeeId={group.employeeId}
									to={`/times/${group.employeeId}/employee`}
									className="employee-name-link"
								>
									{group.employeeName}
								</EmployeeLink>
								{record.employee?.code ? (
									<span className="employee-id"> · ID #{record.employee.code}</span>
								) : (
									""
								)}
							</div>
							<div className="employee-meta">
								{(() => {
									const ubication = record.employee?.employee_with_ubication?.ubication;
									if (!ubication?.name) return null;
									return (
										<span
											className="employee-badge-location"
											style={{
												backgroundColor: ubication?.background_color || "transparent",
												border: "1px solid #dee2e6",
												color: ubication?.color || "#065f46",
											}}
										>
											{ubication?.name}
										</span>
									);
								})()}
								{(() => {
									const funct =
										record.employee?.employee_with_function?.function?.name ||
										record.employee?.position ||
										record.employee?.job_title?.name;
									if (!funct) return null;
									return <span className="employee-role">{funct}</span>;
								})()}
							</div>
						</div>
					</div>
				);
			},
		},
		{
			key: "day",
			header: t("attendance.day"),
			className: "day-cell",
			headerClassName: sortTh("day", "day-cell "),
			sortable: isSortableField("day"),
			render: (row) => {
				const { group, recordIndex } = row;
				if (recordIndex !== 0) return null;
				return formatLocalizedDate(group.day, "weekdayLong");
			},
		},
		{
			key: "planned_shift",
			header: t('common.shift'),
			className: "planned-shift-cell",
			headerClassName: "planned-shift-cell",
			sortable: false,
			render: (row) => {
				const { group, recordIndex } = row;
				if (recordIndex !== 0) return null;
				const dayKey = moment(group.day).format("YYYY-MM-DD");
				const assignments = plannedShifts?.[`${group.employeeId}_${dayKey}`];
				const shiftIntervals = getPlannedShiftIntervals(assignments);
				if (!shiftIntervals?.length) return <span className="no-time">--:--</span>;
				return (
					<div className="planned-shift-list">
						{shiftIntervals.map((shiftInterval, index) => (
							<span
								key={`${group.employeeId}_${dayKey}_${index}`}
								className="planned-shift-text"
							>
								{shiftInterval}
							</span>
						))}
					</div>
				);
			},
		},
		{
			key: "start",
			header: t('attendance.entryStamping'),
			className: "time-cell",
			headerClassName: sortTh("start", "time-cell "),
			sortable: isSortableField("start"),
			render: (row) => {
				const { record } = row;
				return record.start ? (
					<div className="time-cell-content">
						<span className="time">{moment(record.start).format("HH:mm")}</span>
						{renderDeviceIcon(record, "start")}
						{renderSourceIcon(record, "start")}
						{renderLocationIcon(record)}
						{renderModifiedIcon(record, "start")}
					</div>
				) : (
					<span className="no-time">--:--</span>
				);
			},
		},
		{
			key: "end",
			header: t('attendance.exitStamping'),
			className: "time-cell",
			headerClassName: sortTh("end", "time-cell "),
			sortable: isSortableField("end"),
			render: (row) => {
				const { record } = row;
				return record.end ? (
					<div className="time-cell-content">
						<span className="time">{moment(record.end).format("HH:mm")}</span>
						{renderDeviceIcon(record, "end")}
						{renderSourceIcon(record, "end")}
						{renderLocationIcon(record)}
						{renderModifiedIcon(record, "end")}
					</div>
				) : (
					<span className="no-time">--:--</span>
				);
			},
		},
		{
			key: "worked_time",
			header: t('times.table.hours'),
			className: "hours-cell",
			headerClassName: sortTh("worked_time", "hours-cell "),
			sortable: isSortableField("worked_time"),
			render: (row) => {
				const { record } = row;
				return record.start && record.end
					? formatSeconds(calculateDuration(record.start, record.end), "h'")
					: "-";
			},
		},
		{
			key: "type",
			header: t('common.type'),
			className: "type-cell",
			headerClassName: sortTh("type", "type-cell "),
			sortable: isSortableField("type"),
			render: (row) => {
				const { record } = row;
				return (
					<div
						className={`type-badge ${record.type?.name === shiftType.work ? "trabajo" : "other"}`}
					>
						{record.type?.name || "Normal"}
					</div>
				);
			},
		},
		{
			key: "alerts",
			header: "\u00A0",
			className: "alert-column",
			headerClassName: "alert-column",
			sortable: false,
			render: (row) => {
				const { record } = row;
				return (
					<>
						{renderObservationsIcon(record)}
						{record.alerts && record.alerts.length > 0 && renderAlertIcon(record)}
					</>
				);
			},
		},
		{
			key: "total_day",
			header: t('times.table.dayTotal'),
			className: "total-day-cell",
			headerClassName: "primary total-day-cell",
			sortable: false,
			render: (row) => {
				const { group, recordIndex } = row;
				if (recordIndex !== 0) return null;
				return group.totalSeconds > 0 ? formatSeconds(group.totalSeconds, "hm") : "";
			},
		},
		{
			key: "balance",
			header: t('times.table.balance'),
			className: "balance-cell",
			headerClassName: "balance-cell",
			sortable: false,
			render: (row) => {
				const { group, recordIndex } = row;
				if (recordIndex !== 0) return null;
				if (group.balance === null || !group.balanceObj) return null;
				return (
					<OverlayTrigger
						placement="top"
						overlay={
							<Tooltip
								id={`balance-tooltip-${group.id}`}
								className="tooltip-balance tooltip-balance--employee-day"
							>
								<EmployeeDayBalanceTooltip
									group={group}
									timeRegistrationBalanceCalculation={
										timeRegistrationBalanceCalculation
									}
								/>
							</Tooltip>
						}
					>
						<span className="balance-cell__trigger">
							<span className="balance-cell__value">{group.balanceObj.short}</span>
						</span>
					</OverlayTrigger>
				);
			},
		},
		{
			key: "approved",
			header: t('times.table.approved'),
			className: "approved-column",
			headerClassName: sortTh("approved", "approved-column "),
			sortable: isSortableField("approved"),
			align: "center",
			render: (row) => {
				const { record, recordIndex } = row;
				if (recordIndex !== 0) return null;
				return (
					<Switch
						controlId={`approved-${record.id}`}
						isOn={record.approved}
						disabled={approvingIds?.has(String(record.id))}
						handleToggle={() => handleApprovalWithError(record.id, !record.approved)}
					/>
				);
			},
		},
	];

	useEffect(() => {
		const timer = setTimeout(() => {
			const el = document.getElementById("infinite-table-scrollable-target");
			if (el) {
				setHasScroll(el.scrollHeight > el.clientHeight);
			}
		}, 100);
		return () => clearTimeout(timer);
	}, [flatRows]);

	if (loading && groupedItems.length === 0) {
		return (
			<div className="loader-container full-page">
				<div className="loader"></div>
				<div className="loading-text">{t('attendance.loadingData')}</div>
			</div>
		);
	}

	const sortState =
		sortConfig && sortConfig.field
			? { key: sortConfig.field, direction: sortConfig.direction }
			: null;

	const colCount = columns.length + 1;

	const renderCustomRow = (row) => {
		if (row.rowKind === "rest") {
			return (
				<tr className="tr-rest">
					<td colSpan={colCount}>{t('attendance.rest', { duration: row.durationLabel })}</td>
				</tr>
			);
		}
		return null;
	};

	return (
		<DataTable
			className="attendance-supervisor-datatable-scroll"
			tableClassName="times-table table-hover"
			columns={columns}
			rows={flatRows}
			rowKey={(row, idx) =>
				row.rowKind === "rest" ? row.restKey : row.rowKey ?? idx
			}
			emptyMessage={loadingMore ? t('common.loading') : t('attendance.noRecords')}
			sortState={sortState}
			onSortChange={({ key, direction }) => {
				if (isSortableField(key)) {
					applySort(key, direction);
				}
			}}
			renderCustomRow={renderCustomRow}
			actionsColumn={{
				header: t('attendance.viewStampings'),
				headerClassName: "view-stamping-column",
				render: (row) => {
					if (row.rowKind === "rest") return null;
					const { group, recordIndex } = row;
					if (recordIndex !== 0) return null;
					return (
						<Link
							to={`/times/${group.employeeId}/employee`}
							className="view-stamping-link"
							title={t('attendance.viewStampings')}
						>
							<RiEyeLine />
						</Link>
					);
				},
				className: "view-stamping-column",
			}}
			infiniteScroll={{
				dataLength: items?.length || 0,
				next: fetchMoreData,
				hasMore,
				loader:
					hasScroll && loadingMore ? (
						<div className="loading-text text-center py-2">{t('common.loading')}</div>
					) : null,
				endMessage:
					flatRows && flatRows.length > 0 ? (
						<p className="no-more-results text-center">
							{t('times.noMoreRecords')}
						</p>
					) : null,
				scrollThreshold: 0.9,
				scrollableTarget: "infinite-table-scrollable-target",
			}}
			scrollContainerId="infinite-table-scrollable-target"
			scrollContainerClassName="table-responsive attendance-supervisor-scroll-wrap"
		/>
	);
};

export default EmployeeDayTable;
